import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { useAuth } from '../App';
import SecureClientSelector from './SecureClientSelector';
import InvestigationPanel from './InvestigationPanel';
import InterviewUpload from './InterviewUpload';
import ReportViewer from './ReportViewer';
import { buildAttachmentUrl } from '../utils/attachments';
import { formatDateOrDash } from '../utils/dates';
import { getStatusLabel } from '../utils/status';
import { deriveMetricBar } from '../utils/metrics';
import { displayProtocol, displayProtocolTag } from '../utils/protocol';
import { findField, findRoutingField, resolveAnswer } from '../utils/formLabels';
import { getSampleComplaintsByStatus } from '../data/sampleComplaints';
import {
  FileText,
  User,
  Calendar,
  AlertTriangle,
  Clock,
  CheckCircle,
  ChevronRight,
  Search,
  Download,
  Eye,
  Send,
  Shield,
  Activity,
  BarChart3,
  RefreshCw,
  Archive,
  Lock,
  X,
  Plus,
  FileSearch,
  Flag,
  MoreVertical,
  Settings,
  HelpCircle,
  Folder,
  MessageSquare,
  Layers,
  PieChart,
  Briefcase,
  TrendingUp,
  Users,
  CheckSquare,
  Square,
  Camera,
  Filter,
  Info,
  Trash2
} from 'lucide-react';

// Na visão "todos os clientes" o número do caso sozinho é ambíguo: a numeração
// nova reinicia por cliente, então dois clientes podem ter o mesmo 2026-001.
// Com um cliente selecionado a lista já está desambiguada e o nome só polui.
const caseLabelFor = (complaint, selectedClient) => {
  const tag = displayProtocolTag(complaint);
  const isAllClients = !selectedClient || selectedClient === 'all';
  const client = complaint?.client_name || complaint?.client_slug;
  return isAllClients && client ? `${tag} · ${client}` : tag;
};


const DEFAULT_ADMIN_NOTES = {
  status_conclusao: '',
  data_conclusao: '',
  resposta_inicial: '',
  plano_investigacao: '',
  fatos_apurados: '',
  conclusao: '',
  resposta_final: '',
  plano_acao_realizado: ''
};

const normalizeAdminNotes = (notes = {}) => {
  const normalized = { ...DEFAULT_ADMIN_NOTES };
  Object.keys(DEFAULT_ADMIN_NOTES).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(notes, key) && notes[key] !== undefined && notes[key] !== null) {
      normalized[key] = notes[key];
    }
  });
  return normalized;
};

const getLatestLogTimestamp = (logs, field = null) => {
  if (!Array.isArray(logs) || logs.length === 0) {
    return null;
  }

  let latest = 0;
  logs.forEach((log) => {
    if (!log) {
      return;
    }
    if (field && log.field !== field) {
      return;
    }
    const rawDate = log.editedAt || log.edited_at;
    if (!rawDate) {
      return;
    }
    const timestamp = Date.parse(rawDate);
    if (!Number.isNaN(timestamp) && timestamp > latest) {
      latest = timestamp;
    }
  });

  return latest ? new Date(latest).toISOString() : null;
};

const formatSobreQuem = (value) => {
  if (!value) return 'N/A';
  
  const formatMap = {
    'diretores': 'Diretores',
    'presidencia': 'Presidência',
    'demais_colaboradores': 'Demais Colaboradores',
    'fornecedores': 'Fornecedores',
    'clientes': 'Clientes',
    'parceiros_comerciais': 'Parceiros Comerciais'
  };
  
  return formatMap[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

const InvestigationDashboard = ({ demo = false } = {}) => {
  const { user } = useAuth();
  
  // Estados principais
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Filtros e busca
  const [filter, setFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedClient, setSelectedClient] = useState('all'); // Novo filtro de cliente
  
  // View mode
  const [viewMode, setViewMode] = useState('kanban'); // kanban, list, grid
  const [activeTab, setActiveTab] = useState('overview'); // overview, investigation, evidence, timeline, messages, reports, admin
  
  // Estatísticas
  const [stats, setStats] = useState({
    total: 0,
    received: 0,
    investigating: 0,
    resolved: 0,
    closed: 0,
    medium_priority: 0,
    low_priority: 0,
    this_week: 0,
    this_month: 0
  });
  
  // Investigation workflow
  const [investigationNotes, setInvestigationNotes] = useState('');
  const [assignedInvestigator, setAssignedInvestigator] = useState('');
  const [priority, setPriority] = useState('medium');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  
  // Administrative fields
  const [adminNotes, setAdminNotes] = useState({ ...DEFAULT_ADMIN_NOTES });
  const [savingAdminNotes, setSavingAdminNotes] = useState(false);
  const [savingInvestigationNotes, setSavingInvestigationNotes] = useState(false);
  const [lastAdminNotesSavedAt, setLastAdminNotesSavedAt] = useState(null);
  const [lastInvestigationNotesSavedAt, setLastInvestigationNotesSavedAt] = useState(null);
  const [baselineAdminNotes, setBaselineAdminNotes] = useState({ ...DEFAULT_ADMIN_NOTES });
  const [baselineInvestigationNotes, setBaselineInvestigationNotes] = useState('');
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [relatedCases, setRelatedCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  
  // Estados para arquivar denúncia
  const [deleteModal, setDeleteModal] = useState({ open: false, complaintId: null, reason: '' });
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(null);

  // Carregar campos administrativos
  const loadAdminNotes = async (complaintId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      // Escolher rota baseada no tipo de usuário
      const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
      const endpoint = isClientAdmin 
        ? `/api/complaints/client-admin/${complaintId}`
        : `/api/complaints/admin/${complaintId}`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (hasUnsavedChanges) {
          return;
        }

        if (isClientAdmin) {
          const adminData = data?.complaint?.administrative_data || {};
          const mapped = {
            status_conclusao: adminData.status_conclusao || '',
            data_conclusao: adminData.data_conclusao || '',
            resposta_inicial: adminData.resposta_inicial || '',
            plano_investigacao: adminData.plano_investigacao || '',
            fatos_apurados: adminData.fatos_apurados || '',
            conclusao: adminData.conclusao || '',
            resposta_final: adminData.resposta_final || '',
            plano_acao_realizado: adminData.plano_acao_realizado || ''
          };
          const normalized = normalizeAdminNotes(mapped);
          setAdminNotes(normalized);
          setBaselineAdminNotes(normalized);
          setBaselineInvestigationNotes('');
          return;
        }

        const rawNotes = data?.complaint?.admin_notes;
        if (rawNotes) {
          try {
            const parsedNotes = typeof rawNotes === 'string' 
              ? JSON.parse(rawNotes) 
              : rawNotes;
            const normalized = normalizeAdminNotes(parsedNotes);
            setAdminNotes(normalized);
            setBaselineAdminNotes(normalized);
            const investigation = parsedNotes.investigation_notes || '';
            setInvestigationNotes(investigation);
            setBaselineInvestigationNotes(investigation);
            const adminLogs = Array.isArray(parsedNotes.logs) ? parsedNotes.logs : [];
            setLastAdminNotesSavedAt(getLatestLogTimestamp(adminLogs));
            setLastInvestigationNotesSavedAt(getLatestLogTimestamp(adminLogs, 'investigation_notes'));
          } catch (e) {
            console.error('Erro ao fazer parse das admin_notes:', e);
            setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
            setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
            setInvestigationNotes('');
            setBaselineInvestigationNotes('');
          }
        } else {
          setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
          setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
          setInvestigationNotes('');
          setBaselineInvestigationNotes('');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar campos administrativos:', error);
      setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
    }
  };

  // Salvar campos administrativos
  const saveAdminNotes = async () => {
    if (!selectedComplaint) return false;
    
    try {
      setSavingAdminNotes(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      const normalizedAdminNotes = normalizeAdminNotes(adminNotes);
      const baselineNotes = normalizeAdminNotes(baselineAdminNotes);
      const investigationChanged = investigationNotes !== baselineInvestigationNotes;
      const baseNotes = selectedComplaint?.admin_notes && typeof selectedComplaint.admin_notes === 'object'
        ? selectedComplaint.admin_notes
        : {};
      const payloadNotes = {
        ...baseNotes,
        ...normalizedAdminNotes,
        investigation_notes: investigationNotes || ''
      };
      
      // Detectar mudanças
      const changes = Object.keys(normalizedAdminNotes).filter(field => 
        normalizedAdminNotes[field] !== baselineNotes[field]
      ).map(field => ({
        field,
        oldValue: baselineNotes[field] || '',
        newValue: normalizedAdminNotes[field]
      }));
      if (investigationChanged) {
        changes.push({
          field: 'investigation_notes',
          oldValue: baselineInvestigationNotes || '',
          newValue: investigationNotes || ''
        });
      }

      const response = await fetch(`${API_URL}/api/complaints/admin/${selectedComplaint.id}/admin-notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_notes: payloadNotes,
          editor: user?.username || user?.email || 'admin',
          changes
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const lastSavedAt = getLatestLogTimestamp(data.logs) || new Date().toISOString();
        setLastAdminNotesSavedAt(lastSavedAt);
        if (investigationChanged) {
          const lastInvestigationAt = getLatestLogTimestamp(data.logs, 'investigation_notes') || new Date().toISOString();
          setLastInvestigationNotesSavedAt(lastInvestigationAt);
        }
        setBaselineAdminNotes(normalizeAdminNotes(payloadNotes));
        setBaselineInvestigationNotes(payloadNotes.investigation_notes || '');
        toast.success('Campos administrativos salvos com sucesso!');
        // Atualizar o selectedComplaint com os novos dados
        setSelectedComplaint(prev => ({
          ...prev,
          admin_notes: {
            ...(prev?.admin_notes || {}),
            ...payloadNotes,
            ...(Array.isArray(data.logs) ? { logs: data.logs } : {})
          }
        }));
        return true;
      } else {
        throw new Error('Erro ao salvar campos administrativos');
      }
    } catch (error) {
      console.error('Erro ao salvar campos administrativos:', error);
      toast.error('Erro ao salvar campos administrativos');
      return false;
    } finally {
      setSavingAdminNotes(false);
    }
  };

  const saveInvestigationNotes = async () => {
    if (!selectedComplaint) return false;

    try {
      setSavingInvestigationNotes(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      const normalizedAdminNotes = normalizeAdminNotes(adminNotes);
      const baselineNotes = normalizeAdminNotes(baselineAdminNotes);
      const currentNotes = investigationNotes || '';
      const baseNotes = selectedComplaint?.admin_notes && typeof selectedComplaint.admin_notes === 'object'
        ? selectedComplaint.admin_notes
        : {};
      const payloadNotes = {
        ...baseNotes,
        ...normalizedAdminNotes,
        investigation_notes: currentNotes
      };

      const adminChanges = Object.keys(normalizedAdminNotes).filter(field => 
        normalizedAdminNotes[field] !== baselineNotes[field]
      ).map(field => ({
        field,
        oldValue: baselineNotes[field] || '',
        newValue: normalizedAdminNotes[field]
      }));

      const investigationChanges = baselineInvestigationNotes === currentNotes ? [] : [{
        field: 'investigation_notes',
        oldValue: baselineInvestigationNotes || '',
        newValue: currentNotes
      }];
      const changes = [...adminChanges, ...investigationChanges];

      const response = await fetch(`${API_URL}/api/complaints/admin/${selectedComplaint.id}/admin-notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_notes: payloadNotes,
          editor: user?.username || user?.email || 'admin',
          changes
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const lastSavedAt = getLatestLogTimestamp(data.logs, 'investigation_notes') || new Date().toISOString();
        setLastInvestigationNotesSavedAt(lastSavedAt);
        if (adminChanges.length > 0) {
          const lastAdminAt = getLatestLogTimestamp(data.logs) || new Date().toISOString();
          setLastAdminNotesSavedAt(lastAdminAt);
        }
        setBaselineAdminNotes(normalizeAdminNotes(payloadNotes));
        setBaselineInvestigationNotes(payloadNotes.investigation_notes || '');
        setSelectedComplaint(prev => ({
          ...prev,
          admin_notes: {
            ...(prev?.admin_notes || {}),
            ...payloadNotes,
            ...(Array.isArray(data.logs) ? { logs: data.logs } : {})
          }
        }));
        toast.success('Notas de investigação salvas com sucesso!');
        return true;
      } else {
        throw new Error('Erro ao salvar notas de investigação');
      }
    } catch (error) {
      console.error('Erro ao salvar notas de investigação:', error);
      toast.error('Erro ao salvar notas de investigação');
      return false;
    } finally {
      setSavingInvestigationNotes(false);
    }
  };

  // Handler para mudanças nos campos administrativos
  const handleAdminNotesChange = (field, value) => {
    setAdminNotes(prev => ({ ...prev, [field]: value }));
  };

  const formatLastSaved = (value) => {
    if (!value) {
      return 'Ainda não salvo';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Ainda não salvo';
    }
    return date.toLocaleString('pt-BR');
  };
  
  // Mensagens
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  
  // Verificar permissões - SUPER ADMIN TEM ACESSO TOTAL
  const canInvestigate = user?.role === 'super_admin' ||
                         user?.role === 'admin' ||
                         user?.is_admin || 
                         user?.role === 'client_admin' ||
                         user?.is_client_admin ||
                         user?.is_investigador_do_cliente || 
                         user?.is_investigador_externo ||
                         user?.role === 'investigador_externo';
  
  // Super admin nunca é "client admin only"
  const isClientAdminOnly = user?.role !== 'super_admin' && 
                            user?.role !== 'admin' &&
                            user?.is_client_admin && 
                            !user?.is_investigador_do_cliente;
  
  // Client-admin não pode editar NADA no modal
  const isClientAdminReadOnly = user?.role === 'client_admin' || user?.is_client_admin;
  const canExport = user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin;
  const canSelectClient = user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin;

  const hasAdminNotesChanges = useMemo(() => {
    const current = normalizeAdminNotes(adminNotes);
    const baseline = normalizeAdminNotes(baselineAdminNotes);
    return Object.keys(DEFAULT_ADMIN_NOTES).some((key) => current[key] !== baseline[key]);
  }, [adminNotes, baselineAdminNotes]);

  const hasInvestigationNotesChanges = useMemo(
    () => investigationNotes !== baselineInvestigationNotes,
    [investigationNotes, baselineInvestigationNotes]
  );

  const hasUnsavedChanges = !isClientAdminReadOnly && (hasAdminNotesChanges || hasInvestigationNotesChanges);
  const isSavingNotes = savingAdminNotes || savingInvestigationNotes;

  const requestCloseModal = () => {
    if (hasUnsavedChanges) {
      setUnsavedPromptOpen(true);
      return;
    }
    setSelectedComplaint(null);
  };

  const handleDiscardChanges = () => {
    setUnsavedPromptOpen(false);
    setSelectedComplaint(null);
  };

  const handleSaveAndClose = async () => {
    if (!selectedComplaint) {
      setUnsavedPromptOpen(false);
      return;
    }

    let ok = true;
    const shouldSaveInvestigation = hasInvestigationNotesChanges && !hasAdminNotesChanges;
    if (hasAdminNotesChanges) {
      ok = await saveAdminNotes();
    }
    if (ok && shouldSaveInvestigation) {
      ok = await saveInvestigationNotes();
    }
    if (ok) {
      setUnsavedPromptOpen(false);
      setSelectedComplaint(null);
    }
  };

  // Status workflow profissional
  const investigationStatuses = [
    { 
      value: 'received', 
      label: 'Nova', 
      color: 'bg-slate-100 text-slate-700 border-slate-300',
      icon: Clock,
      description: 'Aguardando triagem'
    },
    { 
      value: 'investigating', 
      label: 'Investigação', 
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      icon: Search,
      description: 'Em andamento'
    },
    { 
      value: 'resolved', 
      label: 'Resolvida', 
      color: 'bg-green-100 text-green-700 border-green-300',
      icon: CheckCircle,
      description: 'Caso resolvido'
    },
    { 
      value: 'closed', 
      label: 'Arquivada', 
      color: 'bg-gray-100 text-gray-700 border-gray-300',
      icon: Archive,
      description: 'Caso arquivado'
    }
  ];

  // Prioridades
  const priorities = [
    { value: 'critical', label: 'Crítica', color: 'bg-red-600', textColor: 'text-white' },
    { value: 'high', label: 'Alta', color: 'bg-orange-600', textColor: 'text-white' },
    { value: 'medium', label: 'Média', color: 'bg-yellow-600', textColor: 'text-white' },
    { value: 'low', label: 'Baixa', color: 'bg-green-600', textColor: 'text-white' }
  ];

  useEffect(() => {
    if (canInvestigate) {
      loadComplaints();
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, canInvestigate]);

  // Debug de permissões movido para cá para respeitar rules-of-hooks
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 InvestigationDashboard - Verificando permissões:', {
        user: user?.username,
        role: user?.role,
        is_admin: user?.is_admin,
        canInvestigate,
        isClientAdminOnly
      });
      
      if (!canInvestigate) {
        console.error('❌ Acesso negado para investigação!', {
          user: user?.username,
          role: user?.role,
          canInvestigate
        });
      }
    }
  }, [user, canInvestigate, isClientAdminOnly]);

  // Carregar campos administrativos quando uma denúncia for selecionada
  useEffect(() => {
    if (selectedComplaint) {
      loadAdminNotes(selectedComplaint.id);
      // Sincronizar prioridade com o valor da denúncia
      setPriority(selectedComplaint.priority || 'medium');
    } else {
      setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
      setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
      setInvestigationNotes('');
      setBaselineInvestigationNotes('');
      setLastAdminNotesSavedAt(null);
      setLastInvestigationNotesSavedAt(null);
      setUnsavedPromptOpen(false);
    }
  }, [selectedComplaint]);

  // Recarregar evidências quando a tab evidence for selecionada
  useEffect(() => {
    if (activeTab === 'evidence' && selectedComplaint?.id) {
      console.log('🔄 Tab evidence selecionada, recarregando evidências...');
      loadEvidence(selectedComplaint.id);
    }
  }, [activeTab, selectedComplaint?.id]);

  const loadComplaints = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      // Construir URL com filtros
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') {
        params.append('client_slug', selectedClient);
      }
      
      const url = `${API_URL}/api/complaints/analytics/list${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let complaintsData = data.complaints || data.data || [];

      // Enriquecer dados com prioridade baseada em critérios reais
      complaintsData = complaintsData.map(c => {
        // Determinar prioridade baseada em critérios
        let priority = c.priority || 'medium';
        
        // Ajustar prioridade baseada em palavras-chave
        const description = (c.description || c.responses?.descricao_fatos || '').toLowerCase();
        if (description.includes('urgente') || description.includes('grave') || description.includes('crítico')) {
          priority = 'high';
        }
        if (description.includes('assédio') || description.includes('corrupção') || description.includes('fraude')) {
          priority = 'critical';
        }
        
        // Ajustar baseado no tempo decorrido
        const daysOld = Math.floor((new Date() - new Date(c.created_at)) / (1000 * 60 * 60 * 24));
        if (daysOld > 7 && priority === 'medium') {
          priority = 'high';
        }
        if (daysOld > 14 && priority === 'high') {
          priority = 'critical';
        }
        
        // Gerar tarefas baseadas no status
        const tasks = generateTasksForComplaint(c);
        
        return {
          ...c,
          priority,
          assigned_to: c.assigned_to || c.investigator_name || null,
          tags: c.tags || extractTags(c),
          tasks: tasks,
          completion_percentage: calculateCompletionPercentage({...c, tasks}),
          days_old: daysOld
        };
      });

      setComplaints(complaintsData);
    } catch (error) {
      console.error('Erro ao carregar denúncias:', error);
      toast.error('Erro ao carregar denúncias. Tente novamente.');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredComplaints = useMemo(() => {
    let complaintsData = [...complaints];

    if (filter !== 'all') {
      complaintsData = complaintsData.filter((c) => c.status === filter);
    }

    if (priorityFilter !== 'all') {
      complaintsData = complaintsData.filter((c) => c.priority === priorityFilter);
    }

    const searchValue = searchTerm.trim();
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      complaintsData = complaintsData.filter((c) => {
        const description = (c.description || c.responses?.descricao_fatos || '').toLowerCase();
        const protocol = displayProtocol(c).toLowerCase();
        const responsesText = JSON.stringify(c.responses || {}).toLowerCase();
        const tagsText = Array.isArray(c.tags) ? c.tags.join(' ').toLowerCase() : '';
        const sobreQuem = (c.sobre_quem || c.responses?.sobre_quem || '').toLowerCase();

        return (
          c.id?.toString().includes(searchValue) ||
          protocol.includes(searchLower) ||
          description.includes(searchLower) ||
          responsesText.includes(searchLower) ||
          tagsText.includes(searchLower) ||
          sobreQuem.includes(searchLower)
        );
      });
    }

    if (dateRange.start && dateRange.end) {
      complaintsData = complaintsData.filter((c) => {
        const date = new Date(c.created_at);
        return date >= new Date(dateRange.start) && date <= new Date(dateRange.end);
      });
    }

    complaintsData.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (a.priority !== b.priority) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return complaintsData;
  }, [complaints, filter, priorityFilter, searchTerm, dateRange]);

  // Group E: derive the top metric bar from the SAME filtered data that feeds the
  // Kanban columns, so the bar stays consistent with the columns and reacts to the
  // client filter (selectedClient drives loadComplaints -> complaints -> filteredComplaints).
  const metricBar = useMemo(() => deriveMetricBar(filteredComplaints), [filteredComplaints]);

  // Group H (req 2.13): render fictional sample cards ONLY in the demo/prospecting
  // context AND only when there is no real complaint loaded, so sample content never
  // contaminates a real client board or its counts.
  const showDemoCards = demo && complaints.length === 0;

  const loadStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      const response = await fetch(`${API_URL}/api/complaints/analytics/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.stats) {
          setStats({
            total: data.stats.total_complaints || 0,
            received: data.stats.pending || 0,
            investigating: data.stats.investigating || 0,
            resolved: data.stats.resolved || 0,
            closed: data.stats.closed || 0,
            medium_priority: data.stats.medium_priority || 0,
            low_priority: data.stats.low_priority || 0,
            this_week: data.stats.this_week || 0,
            this_month: data.stats.this_month || 0
          });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const requestExport = async (format) => {
    if (!canExport) {
      toast.error('Você não tem permissão para exportar denúncias.');
      return;
    }

    if (!filteredComplaints.length) {
      toast.info('Nenhuma denúncia para exportar com os filtros atuais.');
      return;
    }

    const ids = filteredComplaints
      .map((complaint) => Number(complaint.id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (!ids.length) {
      toast.info('Nenhuma denúncia válida para exportar.');
      return;
    }

    setExporting(format);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      const response = await fetch(`${API_URL}/api/complaints/analytics/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          format,
          ids,
          filters: {
            status: filter,
            priority: priorityFilter,
            client_slug: selectedClient,
            date_start: dateRange.start,
            date_end: dateRange.end,
            search: searchTerm.trim()
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao exportar denúncias.');
      }

      const blob = await response.blob();
      const exportDate = new Date().toISOString().slice(0, 10);
      const defaultExtension = format === 'pdf' ? 'pdf' : 'xlsx';
      let fileName = `denuncias-investigacao-${exportDate}.${defaultExtension}`;
      const disposition = response.headers.get('content-disposition');
      if (disposition) {
        const match = disposition.match(/filename\\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
        const encodedName = match?.[1] || match?.[2];
        if (encodedName) {
          fileName = decodeURIComponent(encodedName);
        }
      }

      downloadBlob(blob, fileName);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error(error.message || 'Erro ao exportar denúncias. Tente novamente.');
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = () => requestExport('xlsx');
  const handleExportPdf = () => requestExport('pdf');

  const loadComplaintDetails = async (complaintId) => {
    setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
    setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
    setInvestigationNotes('');
    setBaselineInvestigationNotes('');
    setLastAdminNotesSavedAt(null);
    setLastInvestigationNotesSavedAt(null);
    setUnsavedPromptOpen(false);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      // Escolher rota baseada no tipo de usuário
      const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
      const endpoint = isClientAdmin 
        ? `/api/complaints/client-admin/${complaintId}`
        : `/api/complaints/admin/${complaintId}`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('🔍 DADOS RECEBIDOS DO BACKEND:', data.complaint);
        console.log('🔍 RESPONSES:', data.complaint.responses);
        console.log('🔍 TIPO_PUBLICO:', data.complaint.responses?.tipo_publico);
        
        // Parsear admin_notes antes de definir no selectedComplaint
        let parsedAdminNotes = {};
        try {
          if (data.complaint.admin_notes) {
            parsedAdminNotes = typeof data.complaint.admin_notes === 'string' 
              ? JSON.parse(data.complaint.admin_notes) 
              : data.complaint.admin_notes;
          }
        } catch (e) {
          console.error('Erro ao parsear admin_notes:', e);
          parsedAdminNotes = {};
        }

        const adminLogs = Array.isArray(parsedAdminNotes.logs) ? parsedAdminNotes.logs : [];
        setLastAdminNotesSavedAt(getLatestLogTimestamp(adminLogs));
        setLastInvestigationNotesSavedAt(getLatestLogTimestamp(adminLogs, 'investigation_notes'));
        setInvestigationNotes(parsedAdminNotes.investigation_notes || '');
        setBaselineInvestigationNotes(parsedAdminNotes.investigation_notes || '');
        
        const complaint = {
          ...data.complaint,
          admin_notes: parsedAdminNotes,  // Garantir que admin_notes é um objeto
          priority: data.complaint.priority || 'medium',
          assigned_to: data.complaint.assigned_to || null,
          tags: data.complaint.tags || [],
          completion_percentage: calculateCompletionPercentage(data.complaint)
        };
        
        console.log('🔍 COMPLAINT FINAL:', complaint);
        setSelectedComplaint(complaint);
        
        // Popular adminNotes com dados administrativos
        try {
          const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
          if (isClientAdmin) {
            const adminData = data.complaint?.administrative_data || {};
            const mapped = {
              status_conclusao: adminData.status_conclusao || '',
              data_conclusao: adminData.data_conclusao || '',
              resposta_inicial: adminData.resposta_inicial || '',
              plano_investigacao: adminData.plano_investigacao || '',
              fatos_apurados: adminData.fatos_apurados || '',
              conclusao: adminData.conclusao || '',
              resposta_final: adminData.resposta_final || '',
              plano_acao_realizado: adminData.plano_acao_realizado || ''
            };
            const normalized = normalizeAdminNotes(mapped);
            setAdminNotes(normalized);
            setBaselineAdminNotes(normalized);
          } else {
            // Primeiro tentar usar parsedAdminNotes
            if (Object.keys(parsedAdminNotes).length > 0) {
              const normalized = normalizeAdminNotes(parsedAdminNotes);
              setAdminNotes(normalized);
              setBaselineAdminNotes(normalized);
            } else if (data.complaint?.administrative_data) {
              const a = data.complaint.administrative_data;
              const normalized = normalizeAdminNotes({
                status_conclusao: a.status_conclusao || '',
                data_conclusao: a.data_conclusao || '',
                resposta_inicial: a.resposta_inicial || '',
                plano_investigacao: a.plano_investigacao || '',
                fatos_apurados: a.fatos_apurados || '',
                conclusao: a.conclusao || '',
                resposta_final: a.resposta_final || '',
                plano_acao_realizado: a.plano_acao_realizado || ''
              });
              setAdminNotes(normalized);
              setBaselineAdminNotes(normalized);
            } else {
              setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
              setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
            }
          }
        } catch (e) {
          console.error('Falha ao mapear campos administrativos:', e);
          setAdminNotes({ ...DEFAULT_ADMIN_NOTES });
          setBaselineAdminNotes({ ...DEFAULT_ADMIN_NOTES });
        }
        
        // Carregar dados relacionados
        loadTimeline(complaintId);
        loadEvidence(complaintId);
        loadMessages(complaintId);
        loadTasks(complaintId);
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes da denúncia');
    }
  };

  const refreshAiWorkflow = async () => {
    if (!selectedComplaint?.id) {
      return;
    }

    await loadComplaintDetails(selectedComplaint.id);
    loadComplaints();
    loadStats();
  };

  const generateTasksForComplaint = (complaint) => {
    const tasks = [];
    const status = complaint.status;
    
    // Tarefas padrão de investigação
    tasks.push({
      id: 1,
      text: 'Análise inicial dos fatos',
      completed: status !== 'received'
    });
    
    tasks.push({
      id: 2,
      text: 'Verificar informações fornecidas',
      completed: ['investigating', 'under_review', 'resolved', 'closed'].includes(status)
    });
    
    tasks.push({
      id: 3,
      text: 'Coleta de evidências adicionais',
      completed: ['under_review', 'resolved', 'closed'].includes(status)
    });
    
    if (complaint.responses?.identificacao === 'Sim') {
      tasks.push({
        id: 4,
        text: 'Contatar denunciante para esclarecimentos',
        completed: ['investigating', 'under_review', 'resolved', 'closed'].includes(status)
      });
    }
    
    tasks.push({
      id: 5,
      text: 'Entrevistar envolvidos',
      completed: ['resolved', 'closed'].includes(status)
    });
    
    tasks.push({
      id: 6,
      text: 'Elaborar relatório de investigação',
      completed: status === 'closed'
    });
    
    tasks.push({
      id: 7,
      text: 'Definir ações corretivas',
      completed: status === 'closed'
    });
    
    return tasks;
  };

  const calculateCompletionPercentage = (complaint) => {
    // Se não há tarefas definidas, usar o sistema antigo baseado em status
    if (!complaint.tasks || complaint.tasks.length === 0) {
      const statusProgress = {
        received: 10,
        investigating: 60,
        under_review: 80,
        resolved: 100,
        closed: 100
      };
      return statusProgress[complaint.status] ?? 0;
    }
    
    // Calcular baseado nas tarefas completadas
    const totalTasks = complaint.tasks.length;
    const completedTasks = complaint.tasks.filter(task => task.completed).length;
    
    if (totalTasks === 0) return 0;
    
    return Math.round((completedTasks / totalTasks) * 100);
  };

  // Drag-and-drop handler para Kanban
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const idString = draggableId.replace('complaint-', '');
    const movedId = parseInt(idString, 10);
    const newStatus = destination.droppableId;

    // Atualiza otimisticamente o estado local
    setComplaints((prev) =>
      prev.map((c) =>
        c.id === movedId
          ? {
              ...c,
              status: newStatus,
              completion_percentage: calculateCompletionPercentage({...c, status: newStatus}),
            }
          : c
      )
    );

    if (selectedComplaint?.id === movedId) {
      setSelectedComplaint((prev) =>
        prev
          ? {
              ...prev,
              status: newStatus,
              completion_percentage: calculateCompletionPercentage({...prev, status: newStatus}),
            }
          : prev
      );
    }

    try {
      await updateStatus(movedId, newStatus);
    } catch (e) {
      toast.error('Falha ao atualizar status. Tente novamente.');
    }
  };

  const extractTags = (complaint) => {
    const tags = [];
    
    // Tag baseada no tipo de denúncia
    if (complaint.responses?.tipo_denuncia) {
      tags.push(complaint.responses.tipo_denuncia);
    }
    
    // Tag baseada no departamento
    if (complaint.responses?.departamento) {
      tags.push(complaint.responses.departamento);
    }
    
    // Tag se é anônimo
    if (complaint.responses?.identificacao === 'Não') {
      tags.push('Anônimo');
    }
    
    // Tag baseada em palavras-chave
    const description = (complaint.description || complaint.responses?.descricao_fatos || '').toLowerCase();
    if (description.includes('assédio')) tags.push('Assédio');
    if (description.includes('fraude')) tags.push('Fraude');
    if (description.includes('corrupção')) tags.push('Corrupção');
    if (description.includes('discriminação')) tags.push('Discriminação');
    if (description.includes('segurança')) tags.push('Segurança');
    
    // Remover duplicatas
    return [...new Set(tags)];
  };

  const loadTimeline = async (complaintId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      // Por enquanto, criar timeline baseada nos dados disponíveis
      const timelineData = [];
      
      // Evento de criação
      if (selectedComplaint?.created_at) {
        timelineData.push({
          id: 1,
          date: selectedComplaint.created_at,
          action: 'Caso criado',
          user: 'Sistema',
          type: 'system',
          description: `Protocolo ${displayProtocolTag(selectedComplaint)} registrado`
        });
      }
      
      // Evento de status atual
      if (selectedComplaint?.status) {
        const statusLabel = getStatusLabel(selectedComplaint.status);
        timelineData.push({
          id: 2,
          date: selectedComplaint.updated_at || selectedComplaint.created_at,
          action: `Status: ${statusLabel}`,
          user: selectedComplaint.assigned_to || user?.username || 'Sistema',
          type: 'status_change',
          description: `Status atual do caso`
        });
      }
      
      // Adicionar eventos salvos anteriormente
      timeline.forEach((event, index) => {
        if (event.type === 'status_change' && event.date !== selectedComplaint?.created_at) {
          timelineData.push({ ...event, id: timelineData.length + 1 });
        }
      });
      
      // Ordenar por data (mais recentes primeiro)
      timelineData.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setTimeline(timelineData);
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
      // Fallback para timeline básica
      setTimeline([{
        id: 1,
        date: selectedComplaint?.created_at || new Date().toISOString(),
        action: 'Caso criado',
        user: 'Sistema',
        type: 'system',
        description: 'Denúncia recebida'
      }]);
    }
  };

  const loadEvidence = async (complaintId) => {
    try {
      console.log('🔍 loadEvidence chamada para complaintId:', complaintId);
      console.log('🔍 selectedComplaint:', selectedComplaint);
      console.log('🔍 selectedComplaint.responses:', selectedComplaint?.responses);
      console.log('🔍 selectedComplaint.responses.arquivo:', selectedComplaint?.responses?.arquivo);
      
      // Verificar se há arquivo anexado
      const evidenceList = [];
      
      // Verificar se há arquivo no campo responses.arquivo
      if (selectedComplaint?.responses?.arquivo) {
        const filePath = selectedComplaint.responses.arquivo;
        const attachmentUrl = buildAttachmentUrl(filePath);
        console.log('📁 Arquivo encontrado no responses.arquivo:', filePath);

        if (attachmentUrl) {
          evidenceList.push({
            id: 1,
            type: 'document',
            name: 'Documento anexado pelo denunciante',
            path: attachmentUrl,
            uploaded_at: selectedComplaint.submitted_at || selectedComplaint.created_at,
            uploaded_by: 'Denunciante',
            size: 'Desconhecido'
          });

          console.log('📎 Evidência encontrada:', {
            filePath,
            attachmentUrl,
            evidenceList
          });
        }
      } else {
        console.log('❌ Nenhum arquivo encontrado em responses.arquivo');
      }
      
      console.log('📋 Evidence list final:', evidenceList);
      setEvidence(evidenceList);
    } catch (error) {
      console.error('Erro ao carregar evidências:', error);
      setEvidence([]);
    }
  };

  const loadMessages = async (complaintId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      const response = await fetch(`${API_URL}/api/complaints/${complaintId}/messages`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📨 Mensagens recebidas do backend:', data);
        
        const messagesList = data.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          user: msg.is_from_admin ? 'Administrador' : 'Sistema',
          date: msg.created_at,
          type: msg.is_from_admin ? 'sent' : 'received'
        }));
        
        console.log('📨 Mensagens mapeadas:', messagesList);
        setMessages(messagesList);
      } else {
        console.error('Erro ao carregar mensagens:', response.statusText);
        setMessages([]);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      setMessages([]);
    }
  };

  const loadTasks = async (complaintId) => {
    try {
      // Criar tarefas baseadas no status e tipo da denúncia
      const tasksList = [];
      const status = selectedComplaint?.status;
      
      // Tarefas padrão de investigação
      tasksList.push({
        id: 1,
        text: 'Análise inicial dos fatos',
        completed: status !== 'received'
      });
      
      tasksList.push({
        id: 2,
        text: 'Verificar informações fornecidas',
        completed: ['investigating', 'under_review', 'resolved', 'closed'].includes(status)
      });
      
      tasksList.push({
        id: 3,
        text: 'Coleta de evidências adicionais',
        completed: ['under_review', 'resolved', 'closed'].includes(status)
      });
      
      if (selectedComplaint?.responses?.identificacao === 'Sim') {
        tasksList.push({
          id: 4,
          text: 'Contatar denunciante para esclarecimentos',
          completed: false
        });
      }
      
      tasksList.push({
        id: 5,
        text: 'Entrevistar envolvidos',
        completed: ['resolved', 'closed'].includes(status)
      });
      
      tasksList.push({
        id: 6,
        text: 'Elaborar relatório de investigação',
        completed: status === 'closed'
      });
      
      tasksList.push({
        id: 7,
        text: 'Definir ações corretivas',
        completed: status === 'closed'
      });
      
      // Adicionar tarefas customizadas salvas anteriormente
      tasks.forEach(task => {
        if (!tasksList.find(t => t.text === task.text)) {
          tasksList.push({ ...task, id: tasksList.length + 1 });
        }
      });
      
      setTasks(tasksList);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      // Tarefas padrão de fallback
      setTasks([
        { id: 1, text: 'Análise inicial dos fatos', completed: false },
        { id: 2, text: 'Coleta de evidências', completed: false },
        { id: 3, text: 'Entrevista com envolvidos', completed: false },
        { id: 4, text: 'Elaboração do relatório', completed: false }
      ]);
    }
  };

  const updatePriority = async (complaintId, newPriority) => {
    if (!complaintId || !newPriority) {
      toast.error('Dados inválidos para atualização de prioridade');
      return;
    }
    
    try {
      setUpdating(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      // Usar a mesma rota de status, mas mantendo o status atual
      const response = await fetch(`${API_URL}/api/complaints/admin/${complaintId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: selectedComplaint.status, // Manter status atual
          priority: newPriority
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Prioridade atualizada com sucesso!');
        
        // Atualizar lista local
        setComplaints(prev => prev.map(c => 
          c.id === complaintId ? { ...c, priority: newPriority } : c
        ));
        
        // Atualizar denúncia selecionada
        if (selectedComplaint?.id === complaintId) {
          setSelectedComplaint(prev => ({ ...prev, priority: newPriority }));
        }
        
        // Atualizar estado local
        setPriority(newPriority);
        
        // Recarregar lista para garantir sincronização visual no Kanban
        loadComplaints();
      }
    } catch (error) {
      console.error('Erro ao atualizar prioridade:', error);
      toast.error('Erro ao atualizar prioridade. Tente novamente.');
    } finally {
      setUpdating(false);
    }
  };

  // Funções para arquivar denúncia
  const handleDeleteClick = (complaintId) => {
    setDeleteModal({ open: true, complaintId, reason: '' });
  };

  const handleDeleteConfirm = async () => {
    const reason = (deleteModal.reason || '').trim();

    if (reason.length < 5) {
      toast.error('Informe o motivo do arquivamento (mínimo 5 caracteres).');
      return;
    }
    
    try {
      setDeleting(deleteModal.complaintId);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      const response = await fetch(`${API_URL}/api/complaints/${deleteModal.complaintId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao deletar denúncia');
      }

      toast.success('Denúncia arquivada com sucesso');
      setDeleteModal({ open: false, complaintId: null, reason: '' });
      
      // Fechar modal de detalhes se estava aberto
      if (selectedComplaint?.id === deleteModal.complaintId) {
        setSelectedComplaint(null);
      }
      
      // Recarregar lista de denúncias
      loadComplaints();
      loadStats();
    } catch (error) {
      console.error('Erro ao arquivar denúncia:', error);
      toast.error(error.message || 'Erro ao arquivar denúncia');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, complaintId: null, reason: '' });
  };

  const updateStatus = async (complaintId, newStatus) => {
    if (!complaintId || !newStatus) {
      toast.error('Dados inválidos para atualização');
      return;
    }
    
    try {
      setUpdating(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      const response = await fetch(`${API_URL}/api/complaints/admin/${complaintId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          notes: investigationNotes || `Status alterado para: ${newStatus}`,
          priority: priority,
          assigned_to: assignedInvestigator,
          updated_by: user?.username || user?.email || 'Sistema',
          updated_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Status atualizado com sucesso!');
        
        // Atualizar lista local
        setComplaints(prev => prev.map(c => 
          c.id === complaintId ? { 
            ...c, 
            status: newStatus,
            completion_percentage: calculateCompletionPercentage({...c, status: newStatus})
          } : c
        ));
        
        // Atualizar denúncia selecionada
        if (selectedComplaint?.id === complaintId) {
          setSelectedComplaint(prev => ({ 
            ...prev, 
            status: newStatus,
            completion_percentage: calculateCompletionPercentage({...prev, status: newStatus})
          }));
        }
        
        // Adicionar à timeline
        const newTimelineEntry = {
          id: timeline.length + 1,
          date: new Date().toISOString(),
          action: 'Status atualizado',
          user: user?.username || 'Investigador',
          type: 'status_change',
          description: `Status alterado para ${newStatus}`
        };
        setTimeline(prev => [newTimelineEntry, ...prev]);
        
        // Recarregar dados
        loadComplaints();
        loadStats();
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status. Tente novamente.');
    } finally {
      setUpdating(false);
    }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    
    const task = {
      id: tasks.length + 1,
      text: newTask,
      completed: false
    };
    
    setTasks([...tasks, task]);
    setNewTask('');
    toast.success('Tarefa adicionada');
  };

  const toggleTask = (taskId) => {
    setTasks(tasks.map(t => 
      t.id === taskId ? { ...t, completed: !t.completed } : t
    ));
  };

  const addTag = () => {
    if (!newTag.trim() || tags.includes(newTag)) return;
    
    setTags([...tags, newTag]);
    setNewTag('');
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const sendMessage = async () => {
    if (!selectedComplaint || !newMessage.trim()) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
      
      const response = await fetch(`${API_URL}/api/complaints/admin/${selectedComplaint.id}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso!');
        
        // Adicionar à lista de mensagens
        const newMsg = {
          id: messages.length + 1,
          text: newMessage,
          user: user?.username || 'Investigador',
          date: new Date().toISOString(),
          type: 'sent'
        };
        setMessages([...messages, newMsg]);
        
        setNewMessage('');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = investigationStatuses.find(s => s.value === status);
    // Neutral icon/color for unmapped/unknown statuses — do NOT fall back to
    // investigationStatuses[0] ("Nova"). Label always comes from the shared util.
    const Icon = statusConfig?.icon || FileText;
    const color = statusConfig?.color || 'bg-gray-100 text-gray-700 border-gray-300';

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium border ${color}`}>
        <Icon className="w-3 h-3 mr-1.5" />
        {getStatusLabel(status)}
      </div>
    );
  };

  const getPriorityBadge = (priorityValue) => {
    const priorityConfig = priorities.find(p => p.value === priorityValue) || priorities[2];
    
    return (
      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold shadow-sm border ${priorityConfig.color} ${priorityConfig.textColor}`}>
        <Flag className="w-3 h-3 mr-1" />
        {priorityConfig.label}
      </div>
    );
  };

  // Client-admin agora tem acesso ao sistema de investigação

  if (!canInvestigate) {
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="rf-surface p-12 max-w-md w-full text-center">
          <div className="bg-yellow-100 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <Shield className="w-12 h-12 text-yellow-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Sem Permissão</h2>
          <p className="text-gray-600">
            Você não tem permissão para acessar o painel de investigação.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Debug: Role={user?.role}, Admin={String(user?.is_admin)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-screen">
      {/* Cabeçalho: identidade da tela à esquerda, controles à direita.
          Antes eram três faixas empilhadas (barra branca, faixa de KPI em
          gradiente azul-índigo, barra de filtros), cada uma com o próprio
          padding — o que empurrava o conteúdo real para baixo da dobra. */}
      <div className="rf-screen__head">
        <div>
          <h1 className="rf-screen__title">Centro de Investigação</h1>
          <p className="rf-screen__sub">
            {filteredComplaints.length} caso{filteredComplaints.length === 1 ? '' : 's'} no recorte atual
          </p>
        </div>

        <div className="rf-screen__actions">
          {canSelectClient && (
            <SecureClientSelector
              selectedClient={selectedClient}
              onClientChange={(client) => setSelectedClient(client.client_slug)}
              className="w-56"
              showStats={false}
            />
          )}

          <div className="rf-seg" role="group" aria-label="Modo de visualização">
            {[
              { modo: 'kanban', rotulo: 'Kanban', Icone: Layers },
              { modo: 'list', rotulo: 'Lista', Icone: BarChart3 },
              { modo: 'grid', rotulo: 'Dashboard', Icone: PieChart },
            ].map(({ modo, rotulo, Icone }) => (
              <button
                key={modo}
                type="button"
                onClick={() => setViewMode(modo)}
                aria-pressed={viewMode === modo}
                className={`rf-seg__btn${viewMode === modo ? ' is-on' : ''}`}
              >
                <Icone size={15} aria-hidden="true" />
                {rotulo}
              </button>
            ))}
          </div>

          <button
            onClick={loadComplaints}
            className="rf-iconbtn"
            disabled={loading}
            aria-label="Recarregar casos"
            title="Recarregar"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
          </button>

          <button className="rf-btn rf-btn--primary">
            <Plus size={16} aria-hidden="true" />
            Novo caso
          </button>
        </div>
      </div>

      {/* Indicadores — mesma fonte do Kanban (filteredComplaints). Sem a faixa
          em gradiente que existia antes: o dado é que precisa saltar, não o
          fundo atrás dele. */}
      <div className="rf-stats">
        <div className="rf-stat">
          <p className="rf-stat__label">Total de casos</p>
          <div className="rf-stat__value">{metricBar.total}</div>
        </div>
        <div className="rf-stat">
          <p className="rf-stat__label">Em investigação</p>
          <div className="rf-stat__value">{metricBar.investigating}</div>
        </div>
        <div className="rf-stat">
          <p className="rf-stat__label">Taxa de resolução</p>
          <div className="rf-stat__value">{metricBar.resolutionRate}%</div>
        </div>
      </div>

      {/* Filtros: uma linha só, acima de tudo que eles afetam. */}
      <div className="rf-card" style={{ padding: 16 }}>
        <div className="rf-toolbar">
          <div className="rf-toolbar__grow">
            <div className="rf-search">
              <Search size={17} className="rf-search__icon" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por ID, protocolo, descrição ou tag"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rf-input"
                aria-label="Buscar casos"
              />
            </div>
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rf-select"
            style={{ width: 'auto' }}
            aria-label="Filtrar por status"
          >
            <option value="all">Todos os status</option>
            {investigationStatuses.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="rf-select"
            style={{ width: 'auto' }}
            aria-label="Filtrar por prioridade"
          >
            <option value="all">Todas as prioridades</option>
            {priorities.map((priority) => (
              <option key={priority.value} value={priority.value}>{priority.label}</option>
            ))}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="rf-input"
              style={{ width: 'auto' }}
              aria-label="Data inicial"
            />
            <span style={{ fontSize: '.82rem', color: 'var(--rf-muted)' }}>até</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="rf-input"
              style={{ width: 'auto' }}
              aria-label="Data final"
            />
          </div>

          {canExport && (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={exporting || filteredComplaints.length === 0}
                className="rf-btn rf-btn--ghost"
                style={{ padding: '9px 16px', fontSize: '.8rem' }}
                title="Exportar os casos filtrados para Excel"
              >
                <Download size={15} aria-hidden="true" />
                {exporting === 'xlsx' ? 'Gerando…' : 'Excel'}
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exporting || filteredComplaints.length === 0}
                className="rf-btn rf-btn--ghost"
                style={{ padding: '9px 16px', fontSize: '.8rem' }}
                title="Exportar os casos filtrados para PDF"
              >
                <FileText size={15} aria-hidden="true" />
                {exporting === 'pdf' ? 'Gerando…' : 'PDF'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div>
        {viewMode === 'kanban' && (
          <DragDropContext onDragEnd={isClientAdminReadOnly ? () => {} : handleDragEnd}>
            <div className="rf-kanban">
              {investigationStatuses.map((status) => {
                const statusComplaints = showDemoCards
                  ? getSampleComplaintsByStatus(status.value)
                  : filteredComplaints.filter((c) => c.status === status.value);
                // A cor do status vive num filete de 3px no topo da coluna, não
                // numa faixa em gradiente ocupando o cabeçalho inteiro.
                const corDaEtapa =
                  status.value === 'received' ? '#7C8B9B' :
                  status.value === 'investigating' ? '#009EDB' :
                  status.value === 'resolved' ? '#0A7A3D' :
                  '#38495A';

                return (
                  <Droppable droppableId={status.value} key={status.value}>
                    {(provided, snapshot) => (
                      <div
                        className={`rf-lane${snapshot.isDraggingOver ? ' is-over' : ''}`}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <div className="rf-lane__stripe" style={{ background: corDaEtapa }} />

                        <div className="rf-lane__head">
                          <div style={{ minWidth: 0 }}>
                            <h3 className="rf-lane__name">{status.label}</h3>
                            {status.description && (
                              <p className="rf-lane__desc">{status.description}</p>
                            )}
                          </div>
                          <span className="rf-lane__count">{statusComplaints.length}</span>
                        </div>

                        <div className="rf-lane__body">
                          {statusComplaints.length === 0 ? (
                            <div className="rf-lane__empty">
                              {isClientAdminReadOnly ? 'Nenhum caso nesta etapa' : 'Nenhum caso — arraste um para cá'}
                            </div>
                          ) : (
                            statusComplaints.map((complaint, index) => (
                              <Draggable
                                draggableId={`complaint-${complaint.id}`}
                                index={index}
                                key={complaint.id}
                                isDragDisabled={isClientAdminReadOnly || showDemoCards}
                              >
                                {(dragProvided, dragSnapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...(isClientAdminReadOnly ? {} : dragProvided.dragHandleProps)}
                                    onClick={() => { if (!showDemoCards) loadComplaintDetails(complaint.id); }}
                                    className={[
                                      'rf-case',
                                      isClientAdminReadOnly ? 'is-readonly' : '',
                                      dragSnapshot.isDragging ? 'is-dragging' : '',
                                      selectedComplaint?.id === complaint.id ? 'is-selected' : '',
                                    ].filter(Boolean).join(' ')}
                                  >
                                    <div className="rf-case__top">
                                      <span className="rf-case__id">
                                        {caseLabelFor(complaint, selectedClient)}
                                      </span>
                                      {getPriorityBadge(complaint.priority)}
                                    </div>

                                    {user?.role === 'client_admin' && !user?.is_admin && (
                                      <div style={{ marginBottom: 8 }}>
                                        <span className="rf-case__kind">
                                          {complaint.responses?.tipo_denuncia || 'Denúncia'}
                                        </span>
                                      </div>
                                    )}

                                    {complaint.responses?.empresa_slug && (
                                      <div style={{ marginBottom: 8 }}>
                                        <span className="rf-case__kind">
                                          {complaint.responses.empresa_slug}
                                        </span>
                                      </div>
                                    )}

                                    <p className="rf-case__text">
                                      {user?.role === 'client_admin' && !user?.is_admin
                                        ? complaint.responses?.descricao_fatos || 'Denúncia recebida'
                                        : complaint.description ||
                                          complaint.responses?.descricao_fatos ||
                                          'Aguardando análise inicial'}
                                    </p>

                                    <div className="rf-case__progress">
                                      <span style={{ width: `${complaint.completion_percentage || 0}%` }} />
                                    </div>

                                    <div className="rf-case__meta">
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                        <Calendar size={12} aria-hidden="true" />
                                        {formatDateOrDash(complaint.created_at)}
                                      </span>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                        {complaint.assigned_to && (
                                          <>
                                            <User size={12} aria-hidden="true" />
                                            {complaint.assigned_to}
                                          </>
                                        )}
                                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                          {complaint.completion_percentage || 0}%
                                        </span>
                                      </span>
                                    </div>

                                    {complaint.tags && complaint.tags.length > 0 && (
                                      <div className="rf-case__tags">
                                        {complaint.tags.slice(0, 3).map((tag) => (
                                          <span key={tag} className="rf-case__tag">{tag}</span>
                                        ))}
                                        {complaint.tags.length > 3 && (
                                          <span className="rf-case__tag">+{complaint.tags.length - 3}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        )}
        {viewMode === 'list' && (
          <div className="rf-table-wrap">
            <table className="rf-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Descrição</th>
                  <th>Atribuído</th>
                  <th>Data</th>
                  <th>Progresso</th>
                </tr>
              </thead>
              <tbody>
                {filteredComplaints.map((complaint) => (
                  <tr
                    key={complaint.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => loadComplaintDetails(complaint.id)}
                  >
                    <td className="rf-num">
                      {user?.role === 'client_admin' && !user?.is_admin ? (
                        <>
                          <div>{complaint.responses?.tipo_denuncia || 'Denúncia'}</div>
                          <div style={{ fontSize: '.72rem', fontWeight: 400, color: 'var(--rf-muted)' }}>
                            {displayProtocolTag(complaint)}
                          </div>
                        </>
                      ) : (
                        displayProtocolTag(complaint)
                      )}
                    </td>
                    <td>{getPriorityBadge(complaint.priority)}</td>
                    <td>{getStatusBadge(complaint.status)}</td>
                    <td style={{ maxWidth: 320 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {complaint.description || complaint.responses?.descricao_fatos || 'Sem descrição'}
                      </span>
                    </td>
                    <td>{complaint.assigned_to || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateOrDash(complaint.created_at)}</td>
                    <td style={{ minWidth: 130 }}>
                      {/* Barra + número: a barra dá a leitura rápida, o número
                          dá o valor exato sem depender de medir pixel. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="rf-case__progress" style={{ flex: 1, marginBottom: 0 }}>
                          <span style={{ width: `${complaint.completion_percentage || 0}%` }} />
                        </div>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '.72rem', color: 'var(--rf-ink-muted)' }}>
                          {complaint.completion_percentage || 0}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredComplaints.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="rf-empty">Nenhum caso neste recorte.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'grid' && (
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              <section className="rf-card">
                <div className="rf-card__head">
                  <h3 className="rf-card__title">Casos recentes</h3>
                  <span className="rf-card__hint">os 5 mais novos do recorte</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredComplaints.slice(0, 5).map((complaint) => (
                    <button
                      key={complaint.id}
                      type="button"
                      onClick={() => loadComplaintDetails(complaint.id)}
                      className="rf-case"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', width: '100%' }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span className="rf-case__id" style={{ display: 'block' }}>
                          {user?.role === 'client_admin' && !user?.is_admin
                            ? complaint.responses?.tipo_denuncia || 'Denúncia'
                            : caseLabelFor(complaint, selectedClient)}
                        </span>
                        <span style={{ fontSize: '.72rem', color: 'var(--rf-muted)' }}>
                          {user?.role === 'client_admin' && !user?.is_admin
                            ? `${displayProtocolTag(complaint)} · ${formatDateOrDash(complaint.created_at)}`
                            : formatDateOrDash(complaint.created_at)}
                        </span>
                      </span>
                      <span style={{ display: 'inline-flex', gap: 6, flex: 'none' }}>
                        {getStatusBadge(complaint.status)}
                        {getPriorityBadge(complaint.priority)}
                      </span>
                    </button>
                  ))}
                  {filteredComplaints.length === 0 && (
                    <div className="rf-empty">Nenhum caso neste recorte.</div>
                  )}
                </div>
              </section>

              {/* Indicadores DERIVADOS DOS DADOS. Este bloco exibia números fixos
                  no código — "87%" de resolução, "12" casos por investigador e
                  "4.8 ⭐" de satisfação. A taxa real é calculada logo acima na
                  mesma tela e não batia com os 87%; casos/investigador e
                  satisfação não existem no sistema. Publicar número inventado
                  num produto de compliance é o mesmo problema do relatório de
                  segurança fabricado que saiu de /seguranca. */}
              <section className="rf-card">
                <div className="rf-card__head">
                  <h3 className="rf-card__title">Indicadores do recorte</h3>
                </div>
                <div className="rf-stats">
                  <div className="rf-stat">
                    <p className="rf-stat__label">Taxa de resolução</p>
                    <div className="rf-stat__value">{metricBar.resolutionRate}%</div>
                  </div>
                  <div className="rf-stat">
                    <p className="rf-stat__label">Em investigação</p>
                    <div className="rf-stat__value">{metricBar.investigating}</div>
                  </div>
                  <div className="rf-stat">
                    <p className="rf-stat__label">Total de casos</p>
                    <div className="rf-stat__value">{metricBar.total}</div>
                  </div>
                </div>
              </section>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              <section className="rf-card">
                <div className="rf-card__head">
                  <h3 className="rf-card__title">Atividade recente</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {timeline.slice(0, 5).map((event) => (
                    <div key={event.id} style={{ display: 'flex', gap: 10 }}>
                      <span
                        className="rf-modal__mark"
                        style={{ width: 28, height: 28, background: 'rgba(0,158,219,.12)', color: '#00688F' }}
                      >
                        <Activity size={14} aria-hidden="true" />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--rf-ink)' }}>{event.action}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '.72rem', color: 'var(--rf-muted)' }}>
                          {event.user} · {new Date(event.date).toLocaleTimeString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {timeline.length === 0 && (
                    <div className="rf-empty">Sem atividade registrada.</div>
                  )}
                </div>
              </section>

              {/* Ações rápidas: eram quatro botões em quatro cores diferentes
                  (azul, roxo, verde, laranja) sem hierarquia entre si. Só a
                  primeira cria algo; as outras são navegação. */}
              <section className="rf-card">
                <div className="rf-card__head">
                  <h3 className="rf-card__title">Ações rápidas</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="rf-btn rf-btn--primary" style={{ justifyContent: 'flex-start' }}>
                    <Plus size={15} aria-hidden="true" />
                    Criar novo caso
                  </button>
                  <button className="rf-btn rf-btn--ghost" style={{ justifyContent: 'flex-start' }}>
                    <FileSearch size={15} aria-hidden="true" />
                    Busca avançada
                  </button>
                  <button
                    className="rf-btn rf-btn--ghost"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={handleExportPdf}
                    disabled={exporting || filteredComplaints.length === 0}
                  >
                    <Download size={15} aria-hidden="true" />
                    Gerar relatório
                  </button>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Caso */}
      {selectedComplaint && (
        <div className="rf-modal">
          <div className="rf-modal__panel rf-modal__panel--wide">
            {/* Cabeçalho do modal: título + ações numa linha, abas na de baixo.
                A variante --stack existe porque .rf-modal__head é flex-row — sem
                ela, as abas iam parar ao lado do título. */}
            <div className="rf-modal__head rf-modal__head--brand rf-modal__head--stack">
              <div className="rf-modal__headrow">
                <div style={{ minWidth: 0 }}>
                  <h2 className="rf-modal__title">
                    {user?.role === 'client_admin' && !user?.is_admin
                      ? selectedComplaint.responses?.tipo_denuncia || 'Denúncia'
                      : `Caso ${displayProtocolTag(selectedComplaint)}`}
                  </h2>
                  <p className="rf-modal__sub">
                    Criado em {formatDateOrDash(selectedComplaint.created_at)}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 4, flex: 'none' }}>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(selectedComplaint.id);
                      }}
                      disabled={deleting === selectedComplaint.id}
                      className="rf-modal__close"
                      title="Arquivar denúncia"
                      aria-label="Arquivar denúncia"
                    >
                      <Archive size={19} aria-hidden="true" />
                    </button>
                  )}
                  <button
                    onClick={requestCloseModal}
                    className="rf-modal__close"
                    aria-label="Fechar"
                    title="Fechar"
                  >
                    <X size={19} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <nav className="rf-modaltabs" aria-label="Seções do caso">
                {(() => {
                  const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
                  const isAdmin = user?.is_admin;
                  const isInvestigator =
                    user?.role === 'investigador_externo' || user?.role === 'investigador_do_cliente';

                  let availableTabs = ['overview', 'timeline', 'messages'];
                  if (isAdmin || isInvestigator) {
                    availableTabs = ['overview', 'investigation', 'evidence', 'timeline', 'messages', 'reports', 'admin'];
                  } else if (isClientAdmin) {
                    availableTabs = ['overview', 'timeline', 'messages', 'admin'];
                  }

                  const rotulos = {
                    overview: 'Visão geral',
                    investigation: 'Investigação',
                    evidence: 'Evidências',
                    timeline: 'Histórico',
                    messages: 'Mensagens',
                    reports: 'Relatórios',
                    admin: 'Plano de ação',
                  };

                  return availableTabs.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      aria-current={activeTab === tab ? 'true' : undefined}
                      className={`rf-modaltab${activeTab === tab ? ' is-active' : ''}`}
                    >
                      {rotulos[tab]}
                    </button>
                  ));
                })()}
              </nav>
            </div>

            {/* Modal Body com rolagem interna */}
            <div className="p-6 overflow-y-auto flex-1">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Info */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Status and Priority */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Status e Prioridade</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status Atual
                          </label>
                          <select
                            value={selectedComplaint.status}
                            onChange={(e) => updateStatus(selectedComplaint.id, e.target.value)}
                            disabled={updating || isClientAdminReadOnly}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {investigationStatuses.map(status => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prioridade
                          </label>
                          <select
                            value={priority}
                            onChange={(e) => updatePriority(selectedComplaint.id, e.target.value)}
                            disabled={updating || isClientAdminReadOnly}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {priorities.map(p => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Progress */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Progresso</span>
                          <span className="text-sm text-gray-600">{selectedComplaint.completion_percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${selectedComplaint.completion_percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Descrição do Caso</h3>
                      <p className="text-gray-700">
                        {selectedComplaint.description || 
                         selectedComplaint.responses?.descricao_fatos || 
                         selectedComplaint.responses?.relato_detalhado ||
                         'Sem descrição disponível'}
                      </p>
                    </div>

                    {/* Dados da Denúncia */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Dados da Denúncia</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Tipo de Denúncia */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Tipo de Denúncia</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.tipo_denuncia || 'N/A'}
                          </p>
                        </div>

                        {/* Público */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Público</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.tipo_publico || 'N/A'}
                          </p>
                        </div>

                        {/* Denunciante */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Denunciante</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.admin_notes?.denunciante || 'N/A'}
                          </p>
                        </div>

                        {/* Denunciado */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Denunciado(a)</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.admin_notes?.denunciado || 'N/A'}
                          </p>
                        </div>

                        {/* Sobre Quem */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Sobre Quem</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {/* sobre_quem da denúncia guarda a unidade/filial quando é outra a pergunta que divide a visualização */}
                            {/* texto da opção no formulário do cliente: o slug engana (na atomplast, `presidencia` é "Recursos Humanos/Supervisão PCP") */}
                            {formatSobreQuem(resolveAnswer(
                              selectedComplaint.form_fields,
                              'sobre_quem',
                              (findRoutingField(selectedComplaint.form_fields) === findField(selectedComplaint.form_fields, 'sobre_quem') && selectedComplaint.sobre_quem)
                              || selectedComplaint.responses?.sobre_quem
                            ))}
                          </p>
                        </div>

                        {/* Departamento */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Departamento</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.departamento || 'N/A'}
                          </p>
                        </div>

                        {/* Data do Fato */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Data do Fato</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.data_fato ? 
                              new Date(selectedComplaint.responses.data_fato).toLocaleDateString('pt-BR') : 
                              'N/A'}
                          </p>
                        </div>

                        {/* Local do Fato */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Local do Fato</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.local_fato || 'N/A'}
                          </p>
                        </div>

                        {/* Identificação */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Identificação</label>
                          <p className="text-sm text-gray-800 bg-white p-2 rounded border">
                            {selectedComplaint.responses?.identificacao || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Relato Detalhado */}
                    {selectedComplaint.responses?.relato_detalhado && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">Relato Detalhado</h3>
                        <p className="text-gray-700 bg-white p-3 rounded border">
                          {selectedComplaint.responses.relato_detalhado}
                        </p>
                      </div>
                    )}

                    {/* Informações Adicionais */}
                    {selectedComplaint.responses && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-4">Informações Adicionais</h3>
                        <div className="space-y-2">
                          {Object.entries(selectedComplaint.responses).map(([key, value]) => {
                            // Esconder campos de identificação para client-admin
                            const isClientAdmin = user?.role === 'client_admin' && !user?.is_admin;
                            const isIdentificationField = ['nome', 'email', 'telefone', 'cpf', 'identificacao'].includes(key.toLowerCase());
                            
                            // Pular campos já mostrados acima
                            const alreadyShown = ['tipo_denuncia', 'tipo_publico', 'departamento', 'data_fato', 'local_fato', 'identificacao', 'relato_detalhado', 'descricao_fatos', 'sobre_quem'].includes(key);
                            
                            if (isClientAdmin && isIdentificationField) {
                              return null; // Não mostrar campos de identificação
                            }
                            
                            if (alreadyShown || !value) {
                              return null; // Pular campos já mostrados ou vazios
                            }
                            
                            return (
                              <div key={key} className="flex">
                                <span className="text-sm font-medium text-gray-600 w-1/3">
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                </span>
                                <span className="text-sm text-gray-800 w-2/3">
                                  {value || 'N/A'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Tasks */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Tarefas da Investigação</h3>
                      <div className="space-y-2">
                        {tasks.map(task => {
                          const isClientAdmin = user?.role === 'client_admin' && !user?.is_admin;
                          return (
                            <div key={task.id} className="flex items-center space-x-3">
                              {isClientAdmin ? (
                                <div className={`p-1 rounded ${
                                  task.completed 
                                    ? 'text-green-600' 
                                    : 'text-gray-400'
                                }`}>
                                  {task.completed ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                </div>
                              ) : (
                                <button
                                  onClick={() => toggleTask(task.id)}
                                  className={`p-1 rounded ${
                                    task.completed 
                                      ? 'text-green-600 hover:bg-green-100' 
                                      : 'text-gray-400 hover:bg-gray-100'
                                  }`}
                                >
                                  {task.completed ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                </button>
                              )}
                              <span className={`text-sm ${task.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                {task.text}
                              </span>
                            </div>
                          );
                        })}
                        
                        {!(user?.role === 'client_admin' && !user?.is_admin) && (
                          <div className="flex items-center space-x-2 mt-3">
                            <input
                              type="text"
                              value={newTask}
                              onChange={(e) => setNewTask(e.target.value)}
                              disabled={isClientAdminReadOnly}
                              placeholder="Adicionar nova tarefa..."
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <button
                              onClick={addTask}
                              disabled={isClientAdminReadOnly}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Assignment */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Atribuição</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Investigador Responsável
                          </label>
                          <select
                            value={assignedInvestigator}
                            onChange={(e) => setAssignedInvestigator(e.target.value)}
                            disabled={isClientAdminReadOnly}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">Não atribuído</option>
                            <option value={user?.username}>{user?.username}</option>
                            <option value="investigator2">Investigador 2</option>
                            <option value="investigator3">Investigador 3</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Tags</h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {tags.map(tag => {
                          const isClientAdmin = user?.role === 'client_admin' && !user?.is_admin;
                          return (
                            <span 
                              key={tag} 
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center space-x-1"
                            >
                              <span>{tag}</span>
                              {!isClientAdmin && (
                                <button
                                  onClick={() => removeTag(tag)}
                                  className="hover:text-blue-900"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          );
                        })}
                      </div>
                      {!(user?.role === 'client_admin' && !user?.is_admin) && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            disabled={isClientAdminReadOnly}
                            placeholder="Adicionar tag..."
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          <button
                            onClick={addTag}
                            disabled={isClientAdminReadOnly}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Quick Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Informações Rápidas</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {user?.role === 'client_admin' && !user?.is_admin ? 'Tipo:' : 'Protocolo:'}
                          </span>
                          <span className="font-medium">
                            {user?.role === 'client_admin' && !user?.is_admin ? (
                              selectedComplaint.responses?.tipo_denuncia || 'Denúncia'
                            ) : (
                              displayProtocol(selectedComplaint)
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Data Criação:</span>
                          <span className="font-medium">
                            {formatDateOrDash(selectedComplaint.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Última Atualização:</span>
                          <span className="font-medium">
                            {formatDateOrDash(selectedComplaint.updated_at || selectedComplaint.created_at)}
                          </span>
                        </div>
                        {selectedComplaint.responses?.tipo_denuncia && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Tipo:</span>
                            <span className="font-medium">{selectedComplaint.responses.tipo_denuncia}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'investigation' && (
                <div className="space-y-6">
                  <InvestigationPanel
                    complaintId={selectedComplaint.id}
                    clientSlug={selectedComplaint.client_slug || selectedComplaint.responses?.empresa_slug || selectedClient}
                    canEdit={!isClientAdminReadOnly}
                    onRefresh={refreshAiWorkflow}
                  />

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Notas de Investigação</h3>
                    <textarea
                      value={investigationNotes}
                      onChange={(e) => setInvestigationNotes(e.target.value)}
                      disabled={isClientAdminReadOnly}
                      placeholder="Adicione suas notas de investigação aqui..."
                      rows="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs text-gray-500">
                        Último salvamento: {formatLastSaved(lastInvestigationNotesSavedAt)}
                      </span>
                      <button 
                        onClick={saveInvestigationNotes}
                        disabled={savingInvestigationNotes || isClientAdminReadOnly}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        {savingInvestigationNotes ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          'Salvar Notas'
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Ações de Investigação</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium">
                        <Users className="w-4 h-4 inline mr-2" />
                        Agendar Entrevista
                      </button>
                      <button className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium">
                        <FileSearch className="w-4 h-4 inline mr-2" />
                        Solicitar Documentos
                      </button>
                      <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium">
                        <Camera className="w-4 h-4 inline mr-2" />
                        Adicionar Evidência
                      </button>
                      <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        Escalar Caso
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reports' && (
                <div className="space-y-6">
                  <InterviewUpload
                    complaintId={selectedComplaint.id}
                    canEdit={!isClientAdminReadOnly}
                    onRefresh={refreshAiWorkflow}
                  />

                  <ReportViewer
                    complaintId={selectedComplaint.id}
                    currentPhase={selectedComplaint.ai_phase}
                    canEdit={!isClientAdminReadOnly}
                    onRefresh={refreshAiWorkflow}
                  />
                </div>
              )}

              {activeTab === 'evidence' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Evidências e Documentos</h3>
                  
                  {evidence.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <FileSearch className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">Nenhuma evidência anexada a este caso</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {evidence.map((ev) => {
                        const API_URL = process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app';
                        const proxyUrl = ev.path.includes('cloudinary.com')
                          ? `${API_URL}/api/file-proxy?url=${encodeURIComponent(ev.path)}`
                          : ev.path;
                        
                        return (
                          <div key={ev.id} className="rf-surface border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-1">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">{ev.name}</h4>
                                  <p className="text-xs text-gray-500">
                                    Enviado por {ev.uploaded_by} em {new Date(ev.uploaded_at).toLocaleDateString('pt-BR')}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {/* Botão Download Direto - Cloudinary */}
                                <a
                                  href={ev.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium flex items-center space-x-1"
                                  title="Download direto (mais rápido)"
                                >
                                  <Download className="w-4 h-4" />
                                  <span>Download</span>
                                </a>
                                
                                {/* Botão Visualizar via Proxy - Sempre funciona */}
                                <a
                                  href={proxyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium flex items-center space-x-1"
                                  title="Visualizar via servidor (sempre funciona)"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>Visualizar</span>
                                </a>
                              </div>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                <span className="font-medium">Tipo:</span> {ev.type} | 
                                <span className="font-medium ml-2">Tamanho:</span> {ev.size || 'Desconhecido'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="bg-blue-50 rounded-lg p-4 mt-4">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Opções de acesso:</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                          <li><strong>Download:</strong> Abre o anexo na origem correta.</li>
                          <li><strong>Visualizar:</strong> Usa proxy quando o anexo está no Cloudinary e acesso direto nos demais casos.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Histórico do Caso</h3>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                    {timeline.map((event, index) => (
                      <div key={event.id} className="relative flex items-start mb-6">
                        <div className="absolute left-4 w-2 h-2 bg-blue-600 rounded-full -translate-x-1/2 mt-1.5"></div>
                        <div className="ml-10 flex-1">
                          <div className="rf-surface border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900">{event.action}</h4>
                              <span className="text-xs text-gray-500">
                                {new Date(event.date).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{event.description}</p>
                            <p className="text-xs text-gray-500 mt-2">Por: {event.user}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'messages' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Comunicação</h3>
                    <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                      {messages.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">Nenhuma mensagem ainda</p>
                      ) : (
                        messages.map(msg => (
                          <div key={msg.id} className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'} mb-3`}>
                            <div className={`max-w-md px-4 py-3 rounded-lg ${
                              msg.type === 'sent' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200 text-gray-800'
                            }`}>
                              <div className="flex items-start justify-between mb-1">
                                <span className="text-xs font-medium opacity-75">
                                  {msg.user}
                                </span>
                                <span className="text-xs opacity-75">
                                  {new Date(msg.date).toLocaleString('pt-BR')}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed">{msg.text}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={isClientAdminReadOnly}
                        placeholder="Digite uma mensagem..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={isClientAdminReadOnly}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'admin' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-6 h-6 mr-2 text-blue-600" />
                      Plano de Ação e Conclusões
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Gerencie o plano de ação e os campos administrativos da denúncia.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status de Conclusão
                        </label>
                        <input
                          type="text"
                          value={adminNotes.status_conclusao}
                          onChange={(e) => handleAdminNotesChange('status_conclusao', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ex: Concluída, Em andamento, Cancelada"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Conclusão
                        </label>
                        <input
                          type="date"
                          value={adminNotes.data_conclusao}
                          onChange={(e) => handleAdminNotesChange('data_conclusao', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Resposta Inicial ao Denunciante
                        </label>
                        <textarea
                          value={adminNotes.resposta_inicial}
                          onChange={(e) => handleAdminNotesChange('resposta_inicial', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Resposta inicial enviada ao denunciante..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Plano de Investigação Recomendado
                        </label>
                        <textarea
                          value={adminNotes.plano_investigacao}
                          onChange={(e) => handleAdminNotesChange('plano_investigacao', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="4"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Descreva o plano de investigação recomendado..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fatos Apurados na Investigação
                        </label>
                        <textarea
                          value={adminNotes.fatos_apurados}
                          onChange={(e) => handleAdminNotesChange('fatos_apurados', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="4"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Descreva os fatos apurados durante a investigação..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Conclusão
                        </label>
                        <textarea
                          value={adminNotes.conclusao}
                          onChange={(e) => handleAdminNotesChange('conclusao', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="4"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Conclusão da investigação..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Resposta ao Denunciante
                        </label>
                        <textarea
                          value={adminNotes.resposta_final}
                          onChange={(e) => handleAdminNotesChange('resposta_final', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Resposta final enviada ao denunciante..."
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="text-blue-600 font-bold">Plano de Ação Realizado</span>
                        </label>
                        <textarea
                          value={adminNotes.plano_acao_realizado}
                          onChange={(e) => handleAdminNotesChange('plano_acao_realizado', e.target.value)}
                          disabled={isClientAdminReadOnly}
                          rows="4"
                          className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                          placeholder="Descreva o plano de ação que foi realizado para resolver a questão..."
                        />
                      </div>
                    </div>
                    
                    <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs text-gray-500">
                        Último salvamento: {formatLastSaved(lastAdminNotesSavedAt)}
                      </span>
                      <button
                        onClick={saveAdminNotes}
                        disabled={savingAdminNotes || isClientAdminReadOnly}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        {savingAdminNotes ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Shield className="w-4 h-4 mr-2" />
                            Salvar Plano de Ação
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {unsavedPromptOpen && (
        <div className="rf-modal z-[70]">
          <div className="rf-modal__panel rf-modal__panel--narrow">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Alterações não salvas</h3>
                  <p className="text-sm text-gray-600">
                    Você digitou informações que ainda não foram salvas.
                  </p>
                </div>
              </div>

              <div className="text-sm text-gray-700 space-y-1 mb-6">
                {hasAdminNotesChanges && <p>• Campos administrativos/Plano de ação</p>}
                {hasInvestigationNotesChanges && <p>• Notas de investigação</p>}
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
                <button
                  onClick={() => setUnsavedPromptOpen(false)}
                  disabled={isSavingNotes}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Continuar editando
                </button>
                <button
                  onClick={handleDiscardChanges}
                  disabled={isSavingNotes}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Sair sem salvar
                </button>
                <button
                  onClick={handleSaveAndClose}
                  disabled={isSavingNotes}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isSavingNotes ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar e sair'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de arquivamento */}
      {deleteModal.open && (
        <div className="rf-modal z-[60]">
          <div className="rf-modal__panel rf-modal__panel--narrow">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Archive className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirmar arquivamento</h3>
                  <p className="text-sm text-gray-600">A denúncia será movida para Arquivadas</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">
                  Motivo do arquivamento (obrigatório)
                </label>
                <textarea
                  value={deleteModal.reason}
                  onChange={(e) => setDeleteModal({ ...deleteModal, reason: e.target.value })}
                  placeholder="Descreva o motivo do arquivamento"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 min-h-[96px]"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleting === deleteModal.complaintId}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={
                    deleting === deleteModal.complaintId ||
                    (deleteModal.reason || '').trim().length < 5
                  }
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleting === deleteModal.complaintId ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Arquivando...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4" />
                      Arquivar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationDashboard;
